import type { ReactNode } from 'react';
import React, { Children, useState, useEffect, useRef, useCallback, useContext } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { hasTouch } from 'Components/utils/feature-testing';
import { ArrowButton } from 'Components/frameworks/arrow-button/ArrowButton';
import { ResponsiveContext } from 'Components/utils/responsive/ResponsiveContext';
import { themes } from './Scrollable.themes';
const WHEEL_DELTA_SCROLL_FACTOR = 50;
const CLICK_DELTA_SCROLL_FACTOR = 200;
const MOUSE_DELTA_SCROLL_FACTOR = 50;
type Props = {
  backwardLabel?: string;
  children: ReactNode | null;
  childrenWrapperClassNames?: string;
  className?: string;
  controlsClassName?: string;
  deltaScrollFactorClick?: number;
  deltaScrollFactorMouse?: number;
  deltaScrollFactorWheel?: number;
  elementWidth?: number;
  forceDisplayControls?: boolean;
  forwardLabel?: string;
  hasNoMouseWheelScroll?: boolean;
  hasControlsOnTouch?: boolean;
  initialPosition?: number | null;
  innerClassName?: string;
  header?: ReactNode | null;
  navPosition?: 'center' | 'side' | 'top';
  theme?: keyof typeof themes;
};

export function Scrollable({
  backwardLabel,
  children,
  childrenWrapperClassNames,
  className,
  controlsClassName,
  deltaScrollFactorClick = CLICK_DELTA_SCROLL_FACTOR,
  deltaScrollFactorMouse = MOUSE_DELTA_SCROLL_FACTOR,
  deltaScrollFactorWheel = WHEEL_DELTA_SCROLL_FACTOR,
  elementWidth = 82,
  forceDisplayControls = false,
  forwardLabel,
  hasNoMouseWheelScroll = false,
  hasControlsOnTouch = false,
  initialPosition = null,
  innerClassName,
  header,
  navPosition = 'center',
  theme = 'default',
}: Props) {
  const [hasScrollLeft, setHasScrollLeft] = useState(false);
  const [hasScrollRight, setHasScrollRight] = useState(false);

  const scrollAreaNodeRef = useRef<HTMLElement | null>(null);
  const scrollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const hasScrollLeftRef = useRef(hasScrollLeft);
  const hasScrollRightRef = useRef(hasScrollRight);
  hasScrollLeftRef.current = hasScrollLeft;
  hasScrollRightRef.current = hasScrollRight;

  const { isRTL } = useContext(ResponsiveContext);

  const processScroll = useCallback(() => {
    const node = scrollAreaNodeRef.current;
    if (!node) {
      return { hasScrollLeft: false, hasScrollRight: false };
    }

    const { scrollLeft, clientWidth, scrollWidth } = node;
    const isDocRTL = document.documentElement.dir === 'rtl';
    const scrollRight = isDocRTL ? scrollLeft < 0 : scrollLeft + clientWidth < scrollWidth;
    const scrollLeftResult = isDocRTL
      ? scrollLeft > -(scrollWidth - clientWidth - 1)
      : scrollLeft > 0;
    return {
      hasScrollLeft: scrollLeftResult,
      hasScrollRight: scrollRight,
    };
  }, []);

  const onScroll = useCallback(() => {
    const state = processScroll();
    setHasScrollLeft(state.hasScrollLeft);
    setHasScrollRight(state.hasScrollRight);
  }, [processScroll]);

  const onMouseWheel = useCallback(
    (e: Event) => {
      const node = scrollAreaNodeRef.current;
      if (!node) {
        return;
      }

      // @ts-expect-error TODO(flow-migration)
      const wheelDelta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail));
      node.scrollLeft -= wheelDelta * deltaScrollFactorWheel;

      if (hasScrollLeftRef.current || hasScrollRightRef.current) {
        e.preventDefault();
      }
    },
    [deltaScrollFactorWheel],
  );

  useEffect(() => {
    const node = scrollAreaNodeRef.current;
    if (!node) {
      return;
    }

    if (hasNoMouseWheelScroll) {
      onScroll();
      return;
    }

    if (typeof window !== 'undefined') {
      node.addEventListener('scroll', onScroll, false);
      node.addEventListener('mousewheel', onMouseWheel, false);
      node.addEventListener('DOMMouseScroll', onMouseWheel, false);

      onScroll();
    }

    return () => {
      if (hasNoMouseWheelScroll) {
        return;
      }

      if (typeof window !== 'undefined') {
        node.removeEventListener('scroll', onScroll, false);
        node.removeEventListener('mousewheel', onMouseWheel, false);
        node.removeEventListener('DOMMouseScroll', onMouseWheel, false);
      }
    };
  }, [hasNoMouseWheelScroll, onScroll, onMouseWheel]);

  useEffect(() => {
    const node = scrollAreaNodeRef.current;
    if (!node) {
      return;
    }

    if (elementWidth && initialPosition) {
      const normalizedPosition = isRTL ? -1 * initialPosition : initialPosition;
      node.scrollLeft = elementWidth * normalizedPosition;
    }
  }, [initialPosition, elementWidth, isRTL]);

  useEffect(() => {
    const { hasScrollLeft: newLeft, hasScrollRight: newRight } = processScroll();
    if (newLeft !== hasScrollLeft || newRight !== hasScrollRight) {
      setHasScrollLeft(newLeft);
      setHasScrollRight(newRight);
    }
  });

  const onMouseDown = useCallback(
    (direction: number) => () => {
      const node = scrollAreaNodeRef.current;
      if (!node) {
        return;
      }

      scrollingRef.current = setInterval(() => {
        if (!scrollAreaNodeRef.current) {
          return;
        }

        scrollAreaNodeRef.current.scrollLeft +=
          (deltaScrollFactorMouse || MOUSE_DELTA_SCROLL_FACTOR) * direction;
        const state = processScroll();
        setHasScrollLeft(state.hasScrollLeft);
        setHasScrollRight(state.hasScrollRight);

        if (
          (direction === -1 && !state.hasScrollLeft) ||
          (direction === 1 && !state.hasScrollRight)
        ) {
          if (scrollingRef.current) {
            clearInterval(scrollingRef.current);
          }
        }
      }, 100);
    },
    [deltaScrollFactorMouse, processScroll],
  );

  const onMouseUp = useCallback(() => {
    if (scrollingRef.current) {
      clearInterval(scrollingRef.current);
    }
  }, []);

  const onClick = useCallback(
    (direction: number) => () => {
      const node = scrollAreaNodeRef.current;
      if (!node) {
        return;
      }

      node.scrollLeft += (deltaScrollFactorClick || CLICK_DELTA_SCROLL_FACTOR) * direction;
      const state = processScroll();
      setHasScrollLeft(state.hasScrollLeft);
      setHasScrollRight(state.hasScrollRight);
    },
    [deltaScrollFactorClick, processScroll],
  );

  const setScrollAreaRef = useCallback((ref: HTMLElement | null) => {
    scrollAreaNodeRef.current = ref;
  }, []);

  if (!Children.count(children)) {
    return null;
  }

  const shouldDisplayControls =
    (hasControlsOnTouch || !hasTouch()) && (hasScrollLeft || hasScrollRight);
  const {
    thControl,
    thControlColor,
    thControlDisabled,
    thControlLeft,
    thControlRight,
    thControlSize,
    thContolTheme,
    thControls,
    thControlsWrapper,
    thInner,
    thRoot,
  } = themes[theme]({
    navPosition,
  });
  const hasControls = shouldDisplayControls || forceDisplayControls;
  const rightScrollBtn = (
    <ArrowButton
      className={classnames(thControl, thControlRight, thControlSize, {
        [thControlDisabled]: !hasScrollRight,
      })}
      color={thControlColor}
      hasNoRtl
      isDisabled={!hasScrollRight}
      key="right"
      label={forwardLabel}
      onClick={onClick(1)}
      onMouseDown={onMouseDown(1)}
      onMouseUp={onMouseUp}
      rotation={180}
      theme={thContolTheme}
    />
  );
  const leftScrollBtn = (
    <ArrowButton
      className={classnames(thControl, thControlLeft, thControlSize, {
        [thControlDisabled]: !hasScrollLeft,
      })}
      color={thControlColor}
      hasNoRtl
      isDisabled={!hasScrollLeft}
      key="left"
      label={backwardLabel}
      onClick={onClick(-1)}
      onMouseDown={onMouseDown(-1)}
      onMouseUp={onMouseUp}
      theme={thContolTheme}
    />
  );
  const scrollButtons = isRTL ? [rightScrollBtn, leftScrollBtn] : [leftScrollBtn, rightScrollBtn];
  return (
    <div className={classnames(thRoot, className)} data-testid="scrollable">
      <div className={classnames('relative overflow-hidden', innerClassName, thInner)}>
        <div
          className={classnames(
            childrenWrapperClassNames,
            'scrollbar-hidden scroll-x tap-highlight-color-transparent scroll-smooth',
          )}
          ref={setScrollAreaRef}
        >
          {children}
        </div>
      </div>
      {(hasControls || !!header) && (
        <div className={thControlsWrapper}>
          {header}
          {hasControls && (
            <div
              className={classnames(controlsClassName, thControls)}
              data-testid="Scrollable-controls"
            >
              {scrollButtons}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
